/**
 * DashboardService — Admin Analytics (Phase 1)
 *
 * Each method calls one analytics endpoint with a date-range filter and
 * gracefully falls back to a small per-section mock fixture if the request
 * fails (network error, 5xx, etc). The fallback is reported via the
 * returned `source: 'live' | 'mock'` so the UI can badge accordingly.
 */

import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  AnalyticsDateRange,
  DataSource,
  SubscriptionAnalyticsOverview,
  SubscriptionRevenue,
  UserAnalyticsOverview,
} from '../../types/api/dashboard';
import { parseSubscriptionAnalyticsOverview } from '../../utils/analyticsSubscriptionNormalize';

export interface AnalyticsResult<T> {
  data: T;
  source: DataSource;
}

// ────────────────────────── Mock fixtures ──────────────────────────
// Small, plausible shapes used when the backend is unreachable so the
// dashboard remains demo-able. They are intentionally minimal.

const MOCK_USER_OVERVIEW: UserAnalyticsOverview = {
  totalUsers: 190,
  newUsers: 4,
  activeUsers: 2,
  tierDistribution: { FREE: 169, PREMIUM: 21 },
  platformDistribution: { MOBILE: 174, TV: 16 },
  registrationTrend: [
    { period: '2026-01-06', value: 1 },
    { period: '2026-01-15', value: 3 },
  ],
};

const MOCK_SUBSCRIPTION_OVERVIEW: SubscriptionAnalyticsOverview = {
  activeSubscriptions: 25,
  expiredSubscriptions: 1,
  cancelledSubscriptions: 0,
  newSubscriptions: 7,
  upcomingExpirations: 0,
  paymentStatusDistribution: { PENDING: 0, COMPLETED: 7, FAILED: 0 },
};

const MOCK_SUBSCRIPTION_REVENUE: SubscriptionRevenue = {
  currency: 'MMK',
  totalRevenue: 1250000,
  revenueTrend: [
    { period: '2026-01-06', value: 50000 },
    { period: '2026-01-13', value: 200000 },
    { period: '2026-01-20', value: 350000 },
    { period: '2026-01-27', value: 650000 },
  ],
};

// ────────────────────────── Service ───────────────────────────────

export class DashboardService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  async getUserAnalyticsOverview(
    range: AnalyticsDateRange,
  ): Promise<AnalyticsResult<UserAnalyticsOverview>> {
    return this.fetchWithFallback<UserAnalyticsOverview>(
      API_ENDPOINTS.ANALYTICS.USERS_OVERVIEW,
      range,
      MOCK_USER_OVERVIEW,
    );
  }

  async getSubscriptionAnalyticsOverview(
    range: AnalyticsDateRange,
  ): Promise<AnalyticsResult<SubscriptionAnalyticsOverview>> {
    try {
      const qp = new URLSearchParams({ from: range.from, to: range.to });
      const url = `${API_ENDPOINTS.ANALYTICS.SUBSCRIPTIONS_OVERVIEW}?${qp.toString()}`;
      const response = await this.httpClient.get<unknown>(url);
      if (!response.success) {
        throw new Error(response.message || 'Analytics request failed');
      }
      const data = parseSubscriptionAnalyticsOverview(response.data);
      return { data, source: 'live' };
    } catch {
      return { data: MOCK_SUBSCRIPTION_OVERVIEW, source: 'mock' };
    }
  }

  async getSubscriptionRevenue(
    range: AnalyticsDateRange,
  ): Promise<AnalyticsResult<SubscriptionRevenue>> {
    return this.fetchWithFallback<SubscriptionRevenue>(
      API_ENDPOINTS.ANALYTICS.SUBSCRIPTIONS_REVENUE,
      range,
      MOCK_SUBSCRIPTION_REVENUE,
    );
  }

  private async fetchWithFallback<T>(
    url: string,
    range: AnalyticsDateRange,
    fallback: T,
  ): Promise<AnalyticsResult<T>> {
    try {
      const qp = new URLSearchParams({ from: range.from, to: range.to });
      const response = await this.httpClient.get<T>(`${url}?${qp.toString()}`);
      if (!response.success) {
        throw new Error(response.message || 'Analytics request failed');
      }
      return { data: response.data, source: 'live' };
    } catch {
      return { data: fallback, source: 'mock' };
    }
  }
}
